import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { sessionStorageEngine } from './BookingWizardDataStore';

interface BookingPrice {
	[bookingId: string]: number; // Booking ID as key, price as value
}

export const WIZARD_BOOKING_PRICING_CACHE_KEY = 'booking-pricing-cache';

interface UseBookingWizardPricingCacheStoreProps {
	bookingPrices: BookingPrice; // Dictionary of booking IDs and their prices
	setBookingPrice: (bookingId: string, price: number) => void;
	removeBookingPrice: (bookingId: string) => void;
	getBookingPrice: (bookingId: string) => number;
	getTotalBookingPrice: (bookingIds: string[] | undefined, bookingIdToExclude?: string) => number;
	clearAllBookings: () => void;
}

export const useBookingWizardPricingCacheStore = create<
	UseBookingWizardPricingCacheStoreProps,
	[['zustand/persist', unknown]]
>(
	persist(
		(set, get) => ({
			bookingPrices: {},
			setBookingPrice: (bookingId: string, price: number) => {
				set(state => ({
					bookingPrices: {
						...state.bookingPrices,
						[bookingId]: price,
					},
				}));
			},
			removeBookingPrice: (bookingId: string) => {
				const { [bookingId]: _, ...remainingBookings } = get().bookingPrices;
				set({ bookingPrices: remainingBookings });
			},
			getBookingPrice: (bookingId: string) => {
				return get().bookingPrices[bookingId] || 0;
			},
			getTotalBookingPrice: (bookingIds: string[] | undefined, bookingIdToExclude?: string) => {
				if (isNullOrUndefined(bookingIds) || bookingIds.length === 0) {
					return 0;
				}
				return bookingIds.reduce((total, bookingId) => {
					if (bookingId === bookingIdToExclude) {
						return total;
					}
					const price = get().bookingPrices[bookingId];
					return total + (price || 0); // Add price if exists, otherwise 0
				}, 0);
			},
			clearAllBookings: () => {
				set({ bookingPrices: {} });
			},
		}),
		{
			name: WIZARD_BOOKING_PRICING_CACHE_KEY,
			storage: createJSONStorage(() => sessionStorage),
		},
	),
);

export function clearBookingPricingCache() {
	sessionStorageEngine.removeItem(WIZARD_BOOKING_PRICING_CACHE_KEY);
	devLog('DEV0017: Clearing booking pricing cache');
}
